import React, { useState, useEffect, useRef } from "react";
import Pie, { ProvidedProps, PieArcDatum } from "@visx/shape/lib/shapes/Pie";
import { scaleOrdinal } from "@visx/scale";
import { Group } from "@visx/group";
import { animated, useTransition, to } from "@react-spring/web";
const DEFAULT_MARGIN = {
  top: 20,
  right: 20,
  bottom: 20,
  left: 20
};
const VIBRANT_BLUE_COLORS = ["#4287f5", "#1e63d9", "#1b4fab", "#3461eb", "#5c8ef9", "#1c4a94", "#3c75d8", "#2552b9", "#3662d9", "#1a3b87"];
const VIBRANT_RED_COLORS = ["#f54242", "#d91e1e", "#ab1b1b", "#eb3434", "#f95c5c", "#941c1c", "#d83c3c", "#b92525", "#d93636", "#871a1a"];
interface Category {
  label: string;
  value: number;
}
type PieProps = {
  width: number;
  height: number;
  categories: Category[];
  subcategories: Category[];
  margin?: typeof DEFAULT_MARGIN;
  animate?: boolean;
  onCategoryClick?: (category: string) => void;
};
const aggregateSmallCategories = (categories: Category[], bottomThreshold: number, individualThreshold: number) => {
  const totalValue = categories.reduce((acc, cur) => acc + cur.value, 0);
  const sortedCategories = [...categories].sort((a, b) => a.value - b.value);
  let accumulatedValue = 0;
  const filteredCategories: Category[] = [];
  let othersValue = 0;
  const othersSubcategories: Category[] = [];
  for (const category of sortedCategories) {
    const categoryShare = category.value / totalValue;
    if (accumulatedValue / totalValue < bottomThreshold) {
      accumulatedValue += category.value;
      if (categoryShare < individualThreshold) {
        othersValue += category.value;
        othersSubcategories.push(category);
      } else {
        filteredCategories.push(category);
      }
    } else {
      filteredCategories.push(category);
    }
  }
  if (othersValue > 0) {
    filteredCategories.push({
      label: "Others",
      value: othersValue
    });
  }
  return {
    filteredCategories,
    othersSubcategories
  };
};
const Tooltip: React.FC<{
  data: Category | null;
  position: {
    x: number;
    y: number;
  };
}> = ({
  data,
  position
}) => {
  if (!data) return null;
  return <div style={{
    position: "absolute",
    top: position.y,
    left: position.x,
    pointerEvents: "none",
    transitionProperty: "transform",
    transitionDuration: "0.1s",
    zIndex: 999
  }} data-sentry-component="Tooltip" data-sentry-source-file="BudgetPieChart.tsx">
      <div className="flex flex-col rounded-lg bg-black/40 p-2 px-4 text-white shadow-md backdrop-blur-md">
        <span>
          <b>{data.label}</b>
        </span>
        <span>{formatNumber(data.value)} LEI</span>
      </div>
    </div>;
};
const formatNumber = (number: number, locale = "ro-RO") => {
  return number.toLocaleString(locale);
};
const AnimatedPie: React.FC<AnimatedPieProps<Category>> = ({
  animate,
  arcs,
  path,
  getKey,
  getColor,
  isSelected,
  isHorizontal,
  onCategoryClick,
  onTooltipDataUpdate
}) => {
  const transitions = useTransition<PieArcDatum<Category>, AnimatedStyles>(arcs, {
    from: animate ? fromLeaveTransition : enterUpdateTransition,
    enter: enterUpdateTransition,
    update: enterUpdateTransition,
    leave: animate ? fromLeaveTransition : enterUpdateTransition,
    keys: getKey
  });
  return <>
      {transitions((props, arc, {
      key
    }) => {
      const [centroidX, centroidY] = path.centroid(arc);
      const hasSpaceForLabel = arc.endAngle - arc.startAngle >= 0.25;
      const radius = Math.sqrt(centroidX * centroidX + centroidY * centroidY);
      const labelX = centroidX + (radius - 140) * Math.cos((arc.startAngle + arc.endAngle) / 2 - Math.PI / 2);
      const labelY = centroidY + (radius - 140) * Math.sin((arc.startAngle + arc.endAngle) / 2 - Math.PI / 2);
      const midAngle = (arc.startAngle + arc.endAngle) / 2;
      const rotateAngle = midAngle > Math.PI ? 90 : -90;
      const angle = !isSelected ? !isHorizontal ? (arc.startAngle + arc.endAngle) / 2 * (180 / Math.PI) + rotateAngle : (arc.startAngle + arc.endAngle) / 2 * (180 / Math.PI) + (midAngle > Math.PI ? -360 : -180) : 0;
      return <g key={key}>
            <animated.path d={to([props.startAngle, props.endAngle], (startAngle, endAngle) => path({
          ...arc,
          startAngle,
          endAngle
        }))} fill={getColor(arc)} style={{
          cursor: "pointer",
          transformOrigin: `${centroidX}px ${centroidY}px`
        }} onClick={() => onCategoryClick?.(arc.data.label)} onMouseEnter={() => {
          onTooltipDataUpdate?.(arc.data);
        }} onMouseLeave={() => {
          onTooltipDataUpdate?.(null);
        }}>
              <title>{arc.data.label}</title>
            </animated.path>
            {hasSpaceForLabel && <animated.g style={{
          opacity: props.opacity
        }}>
                <text x={!isHorizontal ? labelX : centroidX} y={!isHorizontal ? labelY : centroidY} dy=".33em" fontSize={9} textAnchor="middle" pointerEvents="none" fill="#ffffff" transform={`rotate(${angle} ${!isHorizontal ? labelX : centroidX} ${!isHorizontal ? labelY : centroidY})`} className="hidden wide:block">
                  {arc.data.label.length > (!isHorizontal ? 40 : 20) ? `${arc.data.label.slice(0, !isHorizontal ? 30 : 20)}...` : arc.data.label}
                </text>
              </animated.g>}
          </g>;
    })}
    </>;
};
type AnimatedStyles = {
  startAngle: number;
  endAngle: number;
  opacity: number;
  scale: number;
};
const fromLeaveTransition = ({
  endAngle
}: PieArcDatum<any>) => ({
  startAngle: endAngle > Math.PI ? 2 * Math.PI : 0,
  endAngle: endAngle > Math.PI ? 2 * Math.PI : 0,
  opacity: 0,
  scale: 1
});
const enterUpdateTransition = ({
  startAngle,
  endAngle
}: PieArcDatum<any>) => ({
  startAngle,
  endAngle,
  opacity: 1,
  scale: 1
});
type AnimatedPieProps<Datum> = ProvidedProps<Datum> & {
  animate?: boolean;
  getKey: (d: PieArcDatum<Datum>) => string;
  getColor: (d: PieArcDatum<Datum>) => string;
  isSelected?: boolean;
  isHorizontal?: boolean;
  onCategoryClick?: (label: string) => void;
  onTooltipDataUpdate?: (data: PieArcDatum<Datum>["data"] | null) => void;
};
const BudgetPieChart: React.FC<PieProps> = ({
  width,
  height,
  categories,
  subcategories,
  margin = DEFAULT_MARGIN,
  animate = true,
  onCategoryClick
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showOthers, setShowOthers] = useState<boolean>(false);
  const [tooltipData, setTooltipData] = useState<PieArcDatum<Category>["data"] | null>(null);
  const [tooltipPos, setTooltipPos] = useState({
    x: 0,
    y: 0
  });
  useEffect(() => {
    const handleMove = (event: MouseEvent) => {
      const containerRect = containerRef.current?.getBoundingClientRect();
      if (!containerRect) return;
      const x = event.clientX - containerRect.left;
      const y = event.clientY - containerRect.top;
      setTooltipPos({
        x,
        y
      });
    };
    window.addEventListener("mousemove", handleMove);
    return () => window.removeEventListener("mousemove", handleMove);
  }, []);
  if (width < 10) {
    return null;
  }
  const bottomThreshold = 0.15;
  const individualThreshold = 0.1;
  const {
    filteredCategories,
    othersSubcategories
  } = aggregateSmallCategories(categories, bottomThreshold, individualThreshold);
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;
  const radius = Math.min(innerWidth, innerHeight) / 2;
  const centerY = innerHeight / 2;
  const centerX = innerWidth / 2;
  const donutThickness = 50;
  const colorScale = scaleOrdinal({
    domain: filteredCategories.map(category => category.label),
    range: VIBRANT_BLUE_COLORS
  });
  const colorScaleSubcategories = scaleOrdinal({
    domain: subcategories.map(category => category.label),
    range: VIBRANT_RED_COLORS
  });
  const getColor = (arc: PieArcDatum<Category>) => colorScale(arc.data.label);
  const getColorSubcategory = (arc: PieArcDatum<Category>) => colorScaleSubcategories(arc.data.label);
  const handleCategoryClick = (label: string) => {
    if (label === "Others") {
      setShowOthers(!showOthers);
      setSelectedCategory(null);
    } else {
      setShowOthers(false);
      setSelectedCategory(label);
      setSelectedSubcategory(null);
      onCategoryClick?.(label);
    }
  };
  return <div className="relative overflow-visible" ref={containerRef} data-sentry-component="BudgetPieChart" data-sentry-source-file="BudgetPieChart.tsx">
      <Tooltip data={tooltipData} position={tooltipPos} data-sentry-element="Tooltip" data-sentry-source-file="BudgetPieChart.tsx" />
      <svg width={width} height={height} data-sentry-element="svg" data-sentry-source-file="BudgetPieChart.tsx">
        <rect rx={14} width={width} height={height} fill="url('#visx-pie-gradient')" data-sentry-element="rect" data-sentry-source-file="BudgetPieChart.tsx" />
        <Group top={centerY + margin.top} left={centerX + margin.left} data-sentry-element="Group" data-sentry-source-file="BudgetPieChart.tsx">
          <Pie data={showOthers ? othersSubcategories : filteredCategories} pieValue={d => d.value} pieSortValues={() => -1} outerRadius={radius - donutThickness * 1.3} data-sentry-element="Pie" data-sentry-source-file="BudgetPieChart.tsx">
            {pie => <AnimatedPie {...pie} animate={animate} getKey={({
            data
          }) => `${data.label}-${data.value}-categories`} getColor={getColor} isSelected={filteredCategories.length === 1} onCategoryClick={handleCategoryClick} onTooltipDataUpdate={setTooltipData} />}
          </Pie>

          {selectedCategory && !showOthers && <Pie data={selectedSubcategory ? subcategories.filter(sub => sub.label === selectedSubcategory) : subcategories} pieValue={d => d.value} outerRadius={radius} innerRadius={radius - donutThickness} cornerRadius={3} padAngle={0.005}>
              {pie => <AnimatedPie {...pie} animate={animate} getKey={({
            data
          }) => `${data.label}-${data.value}-subcategories`} getColor={getColorSubcategory} isHorizontal onCategoryClick={onCategoryClick} onTooltipDataUpdate={setTooltipData} />}
            </Pie>}
        </Group>
      </svg>
    </div>;
};
export default BudgetPieChart;